import type { IconType } from "react-icons";
import {
  LuCheck,
  LuClock3,
  LuHand,
  LuLockKeyhole,
  LuMinus,
  LuPause,
  LuPencilLine,
  LuTriangleAlert,
  LuUndo2,
} from "react-icons/lu";

/**
 * How a status looks, in one place.
 *
 * Four colours, not seven. The earlier set used a distinct hue per status,
 * which put teal beside green beside orange and asked the reader to tell them
 * apart — unreliable for anyone, and unusable with protanopia, where red
 * appears dark and orange and green converge.
 *
 * So colour carries the coarse judgement only — done, in hand, needs
 * attention, not running — and an icon carries which of them it is. Either
 * channel alone is enough to name the state, which is the property that makes
 * it legible whatever the reader sees.
 */
export type StatusPresentation = {
  /** A Chakra colour palette: one of four. */
  tone: "green" | "blue" | "orange" | "gray";
  icon: IconType;
  label: string;
};

export function statusPresentation(status: string, overdue = false): StatusPresentation {
  // Late outranks the rest. A row somebody has to act on should not be
  // wearing the colour of one they need not, whatever stage it reached.
  if (overdue && status !== "completed") {
    return { tone: "orange", icon: LuTriangleAlert, label: "Overdue" };
  }

  switch (status) {
    case "completed":
      return { tone: "green", icon: LuCheck, label: "Completed" };
    case "awaiting_review":
      return { tone: "blue", icon: LuClock3, label: "Waiting on someone else" };
    // Not a stored status: what "awaiting_review" is to the one person being
    // waited on. Still in hand, so still blue -- the same blue as the button
    // they are about to press.
    case "waiting_on_you":
      return { tone: "blue", icon: LuHand, label: "Waiting on you" };
    case "in_progress":
      return { tone: "blue", icon: LuPencilLine, label: "In progress" };
    case "needs_revision":
      return { tone: "orange", icon: LuUndo2, label: "Needs revision" };
    case "blocked":
      return { tone: "gray", icon: LuLockKeyhole, label: "Waiting on another activity" };
    case "paused":
      return { tone: "gray", icon: LuPause, label: "Paused" };
    case "not_started":
      return { tone: "gray", icon: LuMinus, label: "Not started" };
    default:
      return { tone: "gray", icon: LuMinus, label: status };
  }
}

/**
 * The legend, grouped by colour.
 *
 * Ordered the way the scheme is built rather than the way a status is reached:
 * a reader working out what the colours mean should meet all the green
 * together, then all the blue. Listing them in lifecycle order would scatter
 * the four groups and undo the point of having only four.
 */
export const STATUS_LEGEND: Array<{ status: string; overdue?: boolean }> = [
  // Done
  { status: "completed" },
  // In hand
  { status: "in_progress" },
  { status: "awaiting_review" },
  // Needs attention
  { status: "needs_revision" },
  { status: "not_started", overdue: true },
  // Not running
  { status: "not_started" },
  { status: "blocked" },
  { status: "paused" },
];
